import type { StateCreator } from "../store";
import type { AppStore } from "../index";
import {
  getPlatformStorageItem,
  removePlatformStorageItem,
  setPlatformStorageItem,
} from "@/data/storage/platform_storage";

export interface AiMessage {
  role: "user" | "system" | "assistant";
  content: string;
}

// Messages organized as threads
export type AiMessages = AiMessage[][];

export interface LlmSlice {
  // State
  llmInput: string;
  llmMessages: AiMessages;
  llmWaiting: boolean;

  // Actions
  setLlmInput: (input: string) => void;
  setLlmMessages: (messages: AiMessages) => void;
  addLlmThread: (messages: AiMessage[]) => void;
  addMessageToThread: (threadIndex: number, message: AiMessage) => void;
  setLlmWaiting: (waiting: boolean) => void;
  clearLlmMessages: () => void;
  saveLlmMessages: () => void;
  loadLlmMessages: () => void;
}

export const createLlmSlice: StateCreator<AppStore, unknown, unknown, LlmSlice> = (set, get) => ({
  // Initial state - load from localStorage
  llmInput: "",
  llmMessages: JSON.parse(getPlatformStorageItem("llm-messages") || "[]"),
  llmWaiting: false,

  // Actions
  setLlmInput: (input) =>
    set((state) => {
      state.llmInput = input;
    }),

  setLlmMessages: (messages) =>
    set((state) => {
      state.llmMessages = messages;
    }),

  addLlmThread: (messages) =>
    set((state) => {
      state.llmMessages.push(messages);
      // Persist to localStorage
      setPlatformStorageItem("llm-messages", JSON.stringify(state.llmMessages));
    }),

  addMessageToThread: (threadIndex, message) =>
    set((state) => {
      if (state.llmMessages[threadIndex]) {
        state.llmMessages[threadIndex].push(message);
        // Persist to localStorage
        setPlatformStorageItem("llm-messages", JSON.stringify(state.llmMessages));
      }
    }),

  setLlmWaiting: (waiting) =>
    set((state) => {
      state.llmWaiting = waiting;
    }),

  clearLlmMessages: () =>
    set((state) => {
      state.llmMessages = [];
      removePlatformStorageItem("llm-messages");
    }),

  saveLlmMessages: () => {
    const { llmMessages } = get();
    setPlatformStorageItem("llm-messages", JSON.stringify(llmMessages));
  },

  loadLlmMessages: () =>
    set((state) => {
      state.llmMessages = JSON.parse(getPlatformStorageItem("llm-messages") || "[]");
    }),
});
